import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import type MarketSettingsCentralMappingMappingController from 'lumen/controllers/market/settings/central-mapping/mapping';
import type { CentralMappingOrganizationSettings } from 'lumen/models';
import type { Transition as TransitionWithTo } from 'lumen/services/transition';
import {
  type Breakdown,
  BREAKDOWNS,
  type CentralMappingFilterQueryParams,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export default class MarketSettingsCentralMappingMappingRoute extends Route {
  queryParams = {
    categories: {
      refreshModel: true,
    },
    barBased: {
      refreshModel: true,
    },
    commissionable: {
      refreshModel: true,
    },
    breakdown: {
      refreshModel: true,
    },
    valueType: {
      refreshModel: true,
    },
    filterOption: {
      refreshModel: false,
    },
    pageSize: {
      refreshModel: true,
    },
    page: {
      refreshModel: true,
    },
    chains: {
      refreshModel: true,
    },
    brands: {
      refreshModel: true,
    },
    properties: {
      refreshModel: true,
    },
    sortBy: {
      refreshModel: true,
    },
    sortDirection: {
      refreshModel: true,
    },
    search: {
      refreshModel: true,
    },
    view: {
      refreshModel: true,
    },
    includeArchived: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service declare centralMapping: Services['central-mapping'];
  @service('central-mapping/saving')
  declare centralMappingSaving: Services['central-mapping/saving'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  @tracked transitionHandler: ((transition: Transition) => void) | null = null;

  getMappingEntriesAndPollSavingStatus = async ({
    breakdown,
    valueType,
    pageSize,
    page,
    filterOptions = {},
    includeArchived,
  }: {
    breakdown: Breakdown;
    filterOptions?: CentralMappingFilterQueryParams;
    includeArchived?: boolean;
    page: number;
    pageSize: number;
    valueType: PmsValueType;
  }): Promise<void> => {
    try {
      const mappingEntriesResponse =
        await this.centralMapping.getMappingEntriesTask.perform({
          breakdown,
          valueType,
          pageSize,
          page,
          filterOptions,
          includeArchived,
        });

      if (!mappingEntriesResponse) {
        return;
      }

      if (!this.access.hasCentralMappingMarketEditAccess) {
        return;
      }

      const { savingStatus } = mappingEntriesResponse;

      if (savingStatus?.inProgress) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.centralMappingSaving.pollSavingStatusTask.perform({
          breakdown,
          valueType,
          initialSavingStatus: savingStatus,
          pageSize,
          page,
          filterOptions,
          includeArchived,
        });
      }
    } catch (e) {
      // Ignore ember concurrency task cancellation errors because we want to cancel existing tasks when a new task is started.
      if (!didCancel(e)) {
        // But re-throw the non-cancelation error
        throw e;
      }
    }
  };

  getOrganizationSettingsTask = task({ restartable: true }, async () => {
    try {
      return await this.store.queryRecord<CentralMappingOrganizationSettings>(
        'central-mapping/organization-settings',
        { namespace: APIGATEWAY },
      );
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  async beforeModel(transition: TransitionWithTo): Promise<void> {
    const { breakdown } = transition.to.queryParams as {
      breakdown: Breakdown | undefined;
    };

    if (
      breakdown === BREAKDOWNS.DISTRIBUTION_CHANNEL &&
      !this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.NET_REVENUE_SETTINGS)
    ) {
      this.router.transitionTo('market.settings.central-mapping.menu');
      return;
    }

    // Await the breakdowns task to ensure the available breakdowns are loaded
    // before validating the query params.
    await this.centralMapping.getBreakdownsTask.last;

    void this.getOrganizationSettingsTask.perform();

    // Use paramsFor to obtain the deserialised query params.
    this.transition.validateCentralMappingQueryParams(
      transition,
      this.paramsFor(this.routeName),
    );
  }

  deactivate(): void {
    this.centralMappingSaving.stopPollingSavingStatus();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/require-await
  async model(params: {
    barBased: string;
    brands: string;
    breakdown: Breakdown;
    categories: string;
    chains: string;
    commissionable: string;
    includeArchived: boolean;
    page: number;
    pageSize: number;
    properties: string;
    search: string;
    sortDirection: string;
    valueType: PmsValueType;
    view: string;
  }) {
    const {
      breakdown,
      valueType,
      page,
      pageSize,
      barBased,
      commissionable,
      categories,
      chains,
      brands,
      properties,
      sortDirection,
      search,
      view,
      includeArchived,
    } = params;

    const orderBy = `value_${sortDirection}`;

    const model = {
      mapping: this.centralMapping.getMappingEntriesTask,
      hierarchy: this.centralMapping.getHierarchyTask,
      marketSubscriptions: this.centralMapping.getMarketSubscriptionTask,
      organizationSettings: this.getOrganizationSettingsTask,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      refreshMappingEntries: () =>
        this.getMappingEntriesAndPollSavingStatus({
          breakdown,
          valueType,
          pageSize,
          page,
          filterOptions: {
            chains,
            brands,
            properties,
            orderBy,
            barBased,
            commissionable,
            categories,
            view,
            search,
          },
          includeArchived,
        }),
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.getMappingEntriesAndPollSavingStatus({
      breakdown,
      valueType,
      pageSize,
      page,
      filterOptions: {
        chains,
        brands,
        properties,
        orderBy,
        barBased,
        commissionable,
        categories,
        view,
        search,
      },
      includeArchived,
    });

    return model;
  }

  resetController(
    controller: MarketSettingsCentralMappingMappingController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (this.transitionHandler) {
      controller.router.off('routeWillChange', this.transitionHandler);
      this.transitionHandler = null;
    }

    controller.resetMappingFilters();
    controller.isShowingSegmentationViewModal = false;
  }

  setupController(
    controller: MarketSettingsCentralMappingMappingController,
    model: ModelFrom<MarketSettingsCentralMappingMappingRoute>,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    if (!this.transitionHandler) {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-misused-promises
      this.transitionHandler = (t: Transition) =>
        this.centralMapping.onRouteWillChange(
          this.routeName,
          controller,
          t as TransitionWithTo,
        );
      controller.router.on('routeWillChange', this.transitionHandler);
    }
  }
}
